<template>
  <v-container class="tradingPlan-view" fluid>
    <LoadingOverlay
      style="position: fixed; z-index: 10"
      :loading="loading"
    ></LoadingOverlay>
    <v-row
      v-if="withoutPlan"
      style="height: 70vh"
      class="text-center"
      align="center"
      justify="center"
    >
      <v-col>
        <div class="d-flex justify-center">
          <v-img
            class="mb-7"
            position="center center"
            src="https://hub.swiset.com/wp-content/uploads/2021/03/Trading-Plan-at-Swiset.png"
            max-height="100px"
            contain
          />
        </div>
        <v-btn class="mx-auto" @click="createTradingPlan">
          {{ $t("btnCreate") }}
          <v-icon class="ml-2" color="white" small>fa-plus</v-icon>
        </v-btn>
        <p class="mt-5" v-html="$t('createDescription')" />
      </v-col>
    </v-row>
    <div v-else>
      <v-container class="mt-n3" fluid>
        <v-row>
          <v-col cols="7" class="mt-3">
            <!-- Card de la izquierda -->
            <v-card
              style="background-color: rgba(0, 0, 0, 0)"
              class="text-center px-5 pt-7 pb-3"
              outlined
            >
              <h2 class='mb-3'>{{ $t("rules.title") }}</h2>
              <p>{{ $t("rules.description") }}</p>
              <v-container fluid>
                <v-row class="mb-n3">
                  <v-col cols="1" class="d-flex align-center">
                    <v-switch inset v-model="tradesRestriction" class="my-0" />
                  </v-col>
                  <v-col cols="9" class="text-start">
                    <h3>{{ $t("rules.tradesRestrictionTitle") }}</h3>
                    <p class="mt-1 mb-0">
                      {{ $t("rules.tradesRestrictionDescription") }}
                    </p>
                  </v-col>
                  <v-col cols="2">
                    <v-text-field
                      v-model="tradesRestrictionNumber"
                      :disabled="!tradesRestriction"
                      :label="$t('rules.maxTradesLabel')"
                      :rules="tradesRestrictionRules"
                      type="number"
                      outlined
                      dense
                    />
                  </v-col>
                </v-row>
                <v-row class="border-divider mb-n3">
                  <v-col cols="1" class="d-flex align-center">
                    <v-switch
                      v-model="negativeTradesRestriction"
                      class="my-0"
                      inset
                    />
                  </v-col>
                  <v-col cols="9" class="text-start">
                    <h3>{{ $t("rules.negativeRestrictionTitle") }}</h3>
                    <p class="mt-1 mb-0">
                      {{ $t("rules.negativeRestrictionDescription") }}
                    </p>
                  </v-col>
                  <v-col cols="2">
                    <v-text-field
                      v-model="negativeTradesNumber"
                      :disabled="!negativeTradesRestriction"
                      :rules="negativeTradesRestrictionRules"
                      :label="$t('rules.maxTradesLabel')"
                      type="number"
                      outlined
                      dense
                    />
                  </v-col>
                </v-row>
                <v-row class="border-divider mb-n3">
                  <v-col cols="1" class="d-flex align-center">
                    <v-switch inset v-model="target" class="my-0" />
                  </v-col>
                  <v-col cols="7" class="text-start">
                    <h3>
                      {{ $t("rules.targetTitle") }}
                      <v-chip class="ml-2 mt-n1" text-color="white" x-small>{{
                        $t("rules.positive")
                      }}</v-chip>
                    </h3>
                    <p class="mt-1 mb-0">{{ $t("rules.targetDescription") }}</p>
                  </v-col>
                  <v-col cols="2">
                    <v-select
                      :items="performanceTypes"
                      v-model="targetType"
                      item-text="text"
                      item-value="value"
                      :label="$t('rules.type')"
                      dense
                      outlined
                    />
                  </v-col>
                  <v-col cols="2">
                    <v-text-field
                      v-model="targetNumber"
                      :disabled="!target"
                      :label="$t('rules.targetLabel')"
                      type="number"
                      outlined
                      :rules="targetRules"
                      dense
                    />
                  </v-col>
                </v-row>
                <v-row class="border-divider mb-n3">
                  <v-col cols="1" class="d-flex align-center">
                    <v-switch inset v-model="tradeLossRestriction" class="my-0" />
                  </v-col>
                  <v-col cols="7" class="text-start">
                    <h3>{{ $t("rules.tradeLossRestrictionTitle") }}</h3>
                    <p class="mt-1 mb-0">
                      {{ $t("rules.tradeLossRestrictionDescription") }}
                    </p>
                  </v-col>
                  <v-col cols="2">
                    <v-select
                      :items="performanceTypes"
                      v-model="tradeLossType"
                      item-text="text"
                      item-value="value"
                      :label="$t('rules.type')"
                      dense
                      outlined
                    />
                  </v-col>
                  <v-col cols="2">
                    <v-text-field
                      v-model="tradeLossNumber"
                      :disabled="!tradeLossRestriction"
                      :label="$t('rules.maxLossLabel')"
                      type="number"
                      outlined
                      dense
                    />
                  </v-col>
                </v-row>
                <v-row class="border-divider mb-n3">
                  <v-col cols="1" class="d-flex align-center">
                    <v-switch inset v-model="periodLossRestriction" class="my-0" />
                  </v-col>
                  <v-col cols="6" class="text-start">
                    <h3>{{ $t("rules.periodLossRestrictionTitle") }}</h3>
                    <p class="mt-1 mb-0">
                      {{ $t("rules.periodLossRestrictionDescription") }}
                    </p>
                  </v-col>
                  <v-col cols="3">
                    <v-select
                      :items="periodTypes"
                      v-model="periodLossType"
                      item-text="text"
                      item-value="value"
                      :label="$t('rules.type')"
                      dense
                      outlined
                    />
                  </v-col>
                  <v-col cols="2">
                    <v-text-field
                      v-model="periodLossNumber"
                      :disabled="!periodLossRestriction"
                      :label="$t('rules.maxLossLabel')"
                      type="number"
                      outlined
                      dense
                    />
                  </v-col>
                </v-row>
                <v-row class="border-divider mb-n3">
                  <v-col cols="1" class="d-flex align-center">
                    <v-switch inset v-model="tradeDurationRestriction" class="my-0" />
                  </v-col>
                  <v-col cols="9" class="text-start">
                    <h3>{{ $t("rules.durationRestrictionTitle") }}</h3>
                    <p class="mt-1 mb-0">
                      {{ $t("rules.durationRestrictionDescription") }}
                    </p>
                  </v-col>
                  <v-col cols="2">
                    <v-text-field
                      v-model="tradeDurationNumber"
                      :disabled="!tradeDurationRestriction"
                      :rules="tradeDurationRules"
                      :label="$t('rules.maxMinutesLabel')"
                      type="number"
                      outlined
                      dense
                    />
                  </v-col>
                </v-row>
                <v-row class="border-divider mb-n3">
                  <v-col cols="1" class="d-flex align-center">
                    <v-switch inset v-model="overnightRestriction" class="my-0" />
                  </v-col>
                  <v-col cols="11" class="text-start">
                    <h3>{{ $t("rules.overnightRestrictionTitle") }}</h3>
                    <p class="mt-1 mb-0">
                      {{ $t("rules.overnightRestrictionDescription") }}
                    </p>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>
          </v-col>
          <v-col cols="5">
            <v-row align="center" justify="center">
              <v-col cols="12">
                <v-card
                  style="background-color: rgba(0, 0, 0, 0)"
                  class="mb-5 px-7 pt-10 pb-7 text-center"
                  outlined
                >
                  <h2>{{ $t("calendar.title") }}</h2>
                  <p class="mb-7">{{ $t("calendar.description") }}</p>
                  <v-row align="center" justify="center">
                    <v-col v-bind:key="day.value" v-for="day in tradingDays">
                      <v-btn
                        @click="changeDayStatus(day.value, !day.trade)"
                        :color="day.trade ? 'primary' : 'error'"
                        small
                        fab
                      >
                        <v-icon small>{{
                          day.trade ? "fa-check" : "fa-times"
                        }}</v-icon>
                      </v-btn>
                      <p class="mt-2 mb-0">{{ day.text }}</p>
                    </v-col>
                  </v-row>
                  <div class="d-flex align-center justify-center mx-3">
                    <p class="mr-3 mt-n5">
                      {{ $t("calendar.tradingHours") }}
                    </p>
                    <v-select
                      :items="tradingHours"
                      :menu-props="{ maxHeight: 200 }"
                      v-model="startHourSelected"
                      :label="$t('calendar.startHourLabel')"
                      dense
                      outlined
                    />
                    <p class="mx-2 mt-n5">{{ $t("calendar.to") }}</p>
                    <v-select
                      :items="tradingHours"
                      :menu-props="{ maxHeight: 200 }"
                      v-model="endHourSelected"
                      :label="$t('calendar.endHourLabel')"
                      dense
                      outlined
                    />
                  </div>
                </v-card>
                <v-card
                  style="background-color: rgba(0, 0, 0, 0)"
                  class="px-7 pt-10 pb-7 text-center"
                  outlined
                >
                  <h2>{{ $t("preMarket.title") }}</h2>
                  <p>{{ $t("preMarket.description") }}</p>
                  <v-textarea
                    v-model="preMarketRoutine"
                    class="mt-7"
                    :label="$t('preMarket.title')"
                    :placeholder="$t('preMarket.placeholder')"
                    rows="5"
                    counter="250"
                    auto-grow
                    outlined
                  />
                </v-card>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row class="mt-5 mb-1" align="center" justify="center">
          <v-btn class="ml-3" @click="saveTradingPlan"
            >{{ $t("btnSave_TradingPlan") }}
            <v-icon class="icon-custom ml-2" small>fa-check</v-icon></v-btn
          >
        </v-row>
      </v-container>
    </div>
  </v-container>
</template>

<script>
import Repository from "../../../repositories/insights";
import LoadingOverlay from '@/components/LoadingOverlay.vue';

export default {
  name: "TradingPlan",
  components: {
    LoadingOverlay,
  },
  props: {
    accountId: Number,
  },
  data() {
    return {
      loading: false,
      withoutPlan: true,
      allTradingPlans: [],
      currentTradingPlan: null,
      overallAccountId: "",
      overallAccountName: "Account",
      valid: true,
      performanceTypes: [
        { text: this.$t("rules.monetary"), value: "monetary" },
        { text: this.$t("rules.percentage"), value: "percentage" },
      ],
      periodTypes: [
        { text: this.$t("rules.mon-week"), value: "WEEKLY_MONETARY_LOSS" },
        { text: this.$t("rules.mon-day"), value: "DAILY_MONETARY_LOSS" },
        { text: this.$t("rules.per-week"), value: "WEEKLY_PERCENTAGE_LOSS" },
        { text: this.$t("rules.per-day"), value: "DAILY_PERCENTAGE_LOSS" },
      ],

      // Trades Restriction
      tradesRestriction: false,
      tradesRestrictionNumber: 1,
      tradesRestrictionRules: [
        (t) => !!t || this.$t("alerts.required"),
        (t) => (t && t > 0) || this.$t("alerts.higherZero"),
        (t) =>
          (t && Number.isSafeInteger(Number(t))) ||
          this.$t("alerts.notInteger"),
        (t) => (t && t.length < 3) || `${this.$t("alerts.greater")} 1000`,
      ],

      // Negative Trades Restriction
      negativeTradesRestriction: false,
      negativeTradesNumber: 1,
      negativeTradesRestrictionRules: [
        (t) => !!t || this.$t("alerts.required"),
        (t) => (t && t > 0) || this.$t("alerts.higherZero"),
        (t) =>
          (t && Number.isSafeInteger(Number(t))) ||
          this.$t("alerts.notInteger"),
        (t) => (t && t.length < 3) || `${this.$t("alerts.greater")} 1000`,
      ],

      // Weekly Target
      target: false,
      targetNumber: 1,
      targetType: "",
      targetRules: [
        (t) => !!t || this.$t("alerts.required"),
        (t) => (t && t > 0) || this.$t("alerts.higherZero"),
        (t) => (t && t.length < 7) || `${this.$t("alerts.greater")} 10000000`,
      ],

      // Trade Loss Restriction
      tradeLossRestriction: false,
      tradeLossNumber: 0,
      tradeLossType: "",
      tradeLossRules: [
        (t) => !!t || this.$t("alerts.required"),
        (t) => (t && t > 0) || this.$t("alerts.higherZero"),
        (t) => (t && t.length < 5) || `${this.$t("alerts.greater")} 100000`,
      ],

      // Period Loss Restriction
      periodLossRestriction: false,
      periodLossNumber: 0,
      periodLossType: "",
      periodLossRules: [
        (t) => !!t || this.$t("alerts.required"),
        (t) => (t && t > 0) || this.$t("alerts.higherZero"),
        (t) => (t && t.length < 5) || `${this.$t("alerts.greater")} 100000`,
      ],

      // Trades Restriction
      tradeDurationRestriction: false,
      tradeDurationNumber: 1,
      tradeDurationRules: [
        (t) => !!t || this.$t("alerts.required"),
        (t) => (t && t > 0) || this.$t("alerts.higherZero"),
        (t) => (t && t.length < 5) || `${this.$t("alerts.greater")} 100000`,
      ],

      // Overnight Restriction
      overnightRestriction: false,

      // Calendar Restriction
      tradingHours: [],
      startHourSelected: "",
      endHourSelected: "",
      tradingDays: [
        { text: this.$t("calendar.monday"), value: "monday", trade: false },
        { text: this.$t("calendar.tuesday"), value: "tuesday", trade: false },
        {
          text: this.$t("calendar.wednesday"),
          value: "wednesday",
          trade: false,
        },
        { text: this.$t("calendar.thursday"), value: "thursday", trade: false },
        { text: this.$t("calendar.friday"), value: "friday", trade: false },
        { text: this.$t("calendar.saturday"), value: "saturday", trade: false },
        { text: this.$t("calendar.sunday"), value: "sunday", trade: false },
      ],

      preMarketRoutine: "",
    };
  },
  mounted() {
    this.init();
  },
  watch: {
    accountId() {
      this.init();
    },
  },
  methods: {
    init() {
      this.allTradingPlans = [];
      this.currentTradingPlan = null;

      this.fillHoursAndDays();
      this.loading = true;
      Repository.getUserTradingPlan().then((response) => {
        if (response.status < 400) {
          response.data.forEach((plan) => {
            this.allTradingPlans.push(JSON.parse(plan));
          });
          this.currentTradingPlan = this.allTradingPlans.find(
            (plan) => plan.id === this.accountId
          );
          // eslint-disable-next-line no-console
          console.log(this.currentTradingPlan.tradingPlanInsights);
          this.withoutPlan =
            this.currentTradingPlan.tradingPlanInsights.length === 0;
          this.setCurrentTradingPlan();
          this.loading = false;
        }
      });
    },
    fillHoursAndDays() {
      // Hours
      this.tradingHours = [];
      for (let i = 0; i < 48; i++)
        this.tradingHours.push(
          `${String(Math.floor(i / 2)).padStart(2, "0")}:${
            i % 2 === 0 ? "00" : "30"
          }`
        );
      this.startHourSelected = this.tradingHours[0];
      this.endHourSelected = this.tradingHours[0];

      // Types
      this.tradeLossType = this.performanceTypes[0].value;
      this.targetType = this.performanceTypes[0].value;
      this.periodLossType = this.periodTypes[0].value;
    },
    changeDayStatus(dayValue, newValue) {
      if (this.validateNoTradingDays() || newValue) {
        let foundIndex = this.tradingDays.findIndex(
          (day) => day.value === dayValue
        );
        this.tradingDays[foundIndex].trade = newValue;
      } else alert("You SHOULD trade at least one day");
    },
    validateNoTradingDays() {
      return this.tradingDays.filter((day) => day.trade === true).length > 1;
    },
    setCurrentTradingPlan() {
      if (!this.withoutPlan) {
        let tradesNumber = this.currentTradingPlan.tradingPlanInsights.find(
          (restriction) =>
            restriction.insightType === "TRADES_NUMBER_RESTRICTION"
        );
        if (tradesNumber !== undefined) {
          this.overallAccountId = tradesNumber.id;
          this.tradesRestriction = tradesNumber.active;
          this.tradesRestrictionNumber = Number(
            tradesNumber.dailyTradesNumberRestriction
          );
        }

        let negativeTrades = this.currentTradingPlan.tradingPlanInsights.find(
          (restriction) =>
            restriction.insightType === "NEGATIVE_TRADES_NUMBER_RESTRICTION"
        );
        if (negativeTrades !== undefined) {
          this.negativeTradesRestriction = negativeTrades.active;
          this.negativeTradesNumber = Number(
            negativeTrades.dailyNegativeTradesNumberRestriction
          );
        }

        let targetResult = this.currentTradingPlan.tradingPlanInsights.find(
          (restriction) => restriction.insightType === "PROFIT_RESTRICTION"
        );
        if (targetResult !== undefined) {
          this.target = targetResult.active;
          this.targetNumber = Number(targetResult.profitTarget);
          if (targetResult.profitTargetType !== "NONE")
            this.targetType =
              targetResult.profitTargetType === "WEEKLY_PERCENTAGE_PROFIT"
                ? "percentage"
                : "monetary";
        }

        let tradeLoss = this.currentTradingPlan.tradingPlanInsights.find(
          (restriction) =>
            restriction.insightType === "LOSS_PER_TRADE_RESTRICTION"
        );
        if (tradeLoss !== undefined) {
          this.tradeLossRestriction = tradeLoss.active;
          this.tradeLossNumber = Number(tradeLoss.maxLossPerTrade);
          if (tradeLoss.lossRestrictionType !== "NONE")
            this.tradeLossType =
              tradeLoss.lossRestrictionType === "PERCENTAGE_LOSS"
                ? "percentage"
                : "monetary";
        }

        let periodLoss = this.currentTradingPlan.tradingPlanInsights.find(
          (restriction) => restriction.insightType === "LOSS_RESTRICTION"
        );
        if (periodLoss !== undefined) {
          this.periodLossRestriction = periodLoss.active;
          this.periodLossNumber = Number(periodLoss.maximumPeriodLoss);
          if (periodLoss.lossRestrictionType !== "NONE")
            this.periodLossType = periodLoss.lossRestrictionType;
        }

        let tradeDuration = this.currentTradingPlan.tradingPlanInsights.find(
          (restriction) =>
            restriction.insightType === "TRADE_DURATION_RESTRICTION"
        );
        if (tradeDuration !== undefined) {
          this.tradeDurationRestriction = tradeDuration.active;
          this.tradeDurationNumber = Number(tradeDuration.maximumTradeDuration);
        }

        let overnight = this.currentTradingPlan.tradingPlanInsights.find(
          (restriction) => restriction.insightType === "OVERNIGHT_RESTRICTION"
        );
        if (overnight !== undefined) {
          this.overnightRestriction = overnight.active;
        }

        let calendar = this.currentTradingPlan.tradingPlanInsights.find(
          (restriction) =>
            restriction.insightType === "TRADING_DAYS_RESTRICTION"
        );
        if (calendar !== undefined) {
          calendar.tradingDays.forEach((partialDay) => {
            let indexFoundDay = this.tradingDays.findIndex(
              (day) => day.value === partialDay.toLowerCase()
            );
            this.tradingDays[indexFoundDay].trade = true;
          });
          this.startHourSelected = this.computeHour(calendar.startingHour);
          this.endHourSelected = this.computeHour(calendar.endingHour);
        }

        let routine = this.currentTradingPlan.tradingPlanInsights.find(
          (restriction) =>
            restriction.insightType === "PRE_MARKET_ROUTINE_RESTRICTION"
        );
        if (routine !== undefined)
          this.preMarketRoutine = routine.preMarketRoutine;
        else this.preMarketRoutine = "";
      }
    },
    computeHour(seconds) {
      if (seconds === 86399) return "23:30";
      if (seconds === 0) return "00:00";
      let hours = 0.0,
        minutes = 0.0;
      if (seconds / 3600.0 > 0.0) {
        hours = Math.floor(seconds / 3600.0);
        seconds = seconds - Math.floor(hours) * 3600.0;
      }
      minutes = seconds / 60.0 > 2 ? 30 : 0;
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
        2,
        "0"
      )}`;
    },
    createTradingPlan() {
      Repository.createTradingPlan(this.accountId).then((response) => {
        if (response.status < 400) {
          this.loading = true;
          alert(this.$t("alerts.created"));
          setTimeout(() => {
            this.init();
          }, 7000);
        }
      });
    },
    saveTradingPlan() {
      if (this.validateValues() && this.validateTradingDays()) {
        this.modifyTradesNumberRestriction();
        this.modifyNegativeTradesRestriction();
        this.modifyTargetExpectedResult();
        this.modifyTradeLossRestriction();
        this.modifyPeriodLossRestriction();
        this.modifyTradeDurationRestriction();
        this.modifyOvernightRestriction();
        this.modifyTradingDays();
        this.modifyPreMarketRoutine();
        alert(this.$t("alerts.saved"));
      }
    },
    validateValues() {
      let tradesNumberValidation = this.integerValidation(
        this.tradesRestrictionNumber,
        "Trades"
      );
      if (tradesNumberValidation !== "ok") {
        alert(tradesNumberValidation);
        return false;
      }

      let negativeTradesValidation = this.integerValidation(
        this.negativeTradesNumber,
        "Negative Trades"
      );
      if (negativeTradesValidation !== "ok") {
        alert(negativeTradesValidation);
        return false;
      }

      let targetValidation = this.doubleValidation(
        this.targetNumber,
        "Target Expected"
      );
      if (targetValidation !== "ok") {
        alert(targetValidation);
        return false;
      } else if (this.targetType === "percentage" && this.targetNumber > 100) {
        alert(this.$t("alerts.percentage"));
        return false;
      }

      let tradeLossValidation = this.doubleValidation(
        this.tradeLossNumber,
        "Trade Loss"
      );
      if (tradeLossValidation !== "ok") {
        alert(tradeLossValidation);
        return false;
      } else if (
        this.tradeLossType === "percentage" &&
        this.tradeLossNumber > 100
      ) {
        alert(this.$t("alerts.percentage"));
        return false;
      }

      let periodLossValidation = this.doubleValidation(
        this.periodLossNumber,
        "Period Loss"
      );
      if (periodLossValidation !== "ok") {
        alert(periodLossValidation);
        return false;
      }

      let tradeDurationValidation = this.doubleValidation(
        this.tradeDurationNumber,
        "Trade Duration"
      );
      if (tradeDurationValidation !== "ok") {
        alert(tradeDurationValidation);
        return false;
      }

      return true;
    },
    validateTradingDays() {
      if (
        Number(this.endHourSelected.replace(":", "")) >
        Number(this.startHourSelected.replace(":", ""))
      )
        return true;
      alert(this.$t("alerts.hoursError"));
      return false;
    },
    integerValidation(value, type) {
      if (isNaN(value)) return `${type} ${this.$t("alerts.required")}.`;
      if (Number(value) < 0) return `${type} ${this.$t("alerts.higherZero")}.`;
      if (value.length > 3) return `${type} ${this.$t("alerts.greater")} 1000.`;
      if (!Number.isSafeInteger(Number(value)))
        return `${type} ${this.$t("alerts.notInteger")}.`;
      return "ok";
    },
    doubleValidation(value, type) {
      if (isNaN(value)) return `${type} ${this.$t("alerts.required")}.`;
      if (Number(value) < 0) return `${type} ${this.$t("alerts.higherZero")}.`;
      if (value.length > 5)
        return `${type} ${this.$t("alerts.greater")} 100000.`;
      return "ok";
    },
    modifyTradesNumberRestriction() {
      let tradesNumberRestriction = {
        id: this.overallAccountId,
        insightType: "TRADES_NUMBER_RESTRICTION",
        newActiveState: this.tradesRestriction,
        newPeriodMaximum: this.tradesRestrictionNumber,
        periodType: true,
      };

      Repository.modifyTradesNumberRestriction(tradesNumberRestriction).then(
        (response) => {
          if (response.status > 400)
            alert(
              "An error occurred by modifying the trades number restriction :("
            );
        }
      );
    },
    modifyPreMarketRoutine() {
      let preMarketRoutineDTO = {
        id: this.overallAccountId,
        insightType: "PRE_MARKET_ROUTINE_RESTRICTION",
        newActiveState: true,
        preMarketRoutine: this.preMarketRoutine,
      };

      Repository.modifyPreMarketRoutine(preMarketRoutineDTO).then(
        (response) => {
          if (response.status > 400)
            alert(
              "An error occurred while modifying the pre market routine :("
            );
        }
      );
    },
    modifyNegativeTradesRestriction() {
      let negativeTradesRestriction = {
        id: this.overallAccountId,
        insightType: "NEGATIVE_TRADES_NUMBER_RESTRICTION",
        newActiveState: this.negativeTradesRestriction,
        newPeriodMaximum: this.negativeTradesNumber,
        periodType: true,
      };

      Repository.modifyNegativeTradesRestriction(
        negativeTradesRestriction
      ).then((response) => {
        if (response.status > 400)
          alert(
            "An error occurred by modifying the negative trades restriction :("
          );
      });
    },
    modifyTargetExpectedResult() {
      let tradesNumberRestriction = {
        id: this.overallAccountId,
        insightType: "PROFIT_RESTRICTION",
        newActiveState: this.target,
        newProfitTarget: this.targetNumber,
        profitTargetType:
          this.targetType === "percentage"
            ? "WEEKLY_PERCENTAGE_PROFIT"
            : "WEEKLY_MONETARY_PROFIT",
      };

      Repository.modifyTargetExpectedResult(tradesNumberRestriction).then(
        (response) => {
          if (response.status > 400)
            alert(
              "An error occurred by modifying the target expected result :("
            );
        }
      );
    },
    modifyTradeLossRestriction() {
      let tradeLossRestriction = {
        id: this.overallAccountId,
        insightType: "LOSS_PER_TRADE_RESTRICTION",
        newActiveState: this.tradeLossRestriction,
        maxLossPerTrade: Number(this.tradeLossNumber),
        lossRestrictionType:
          this.tradeLossType === "monetary"
            ? "MONETARY_LOSS"
            : "PERCENTAGE_LOSS",
      };

      // eslint-disable-next-line no-console
      console.log(tradeLossRestriction);

      Repository.modifyTradeLossRestriction(tradeLossRestriction).then(
        (response) => {
          if (response.status > 400)
            alert(
              "An error occurred by modifying the trade loss restriction :("
            );
        }
      );
    },
    modifyPeriodLossRestriction() {
      let periodLossRestriction = {
        id: this.overallAccountId,
        insightType: "LOSS_RESTRICTION",
        newActiveState: this.periodLossRestriction,
        lossRestrictionType: this.periodLossType,
        maximumPeriodLoss: this.periodLossNumber,
      };

      Repository.modifyPeriodLossRestriction(periodLossRestriction).then(
        (response) => {
          if (response.status > 400)
            alert(
              "An error occurred by modifying the trade loss restriction :("
            );
        }
      );
    },
    modifyTradeDurationRestriction() {
      let tradeDurationRestriction = {
        id: this.overallAccountId,
        insightType: "TRADE_DURATION_RESTRICTION",
        newActiveState: this.tradeDurationRestriction,
        newMaximumTradeDuration: this.tradeDurationNumber,
      };

      Repository.modifyTradeDurationRestriction(tradeDurationRestriction).then(
        (response) => {
          if (response.status > 400)
            alert(
              "An error occurred by modifying the trade duration restriction :("
            );
        }
      );
    },
    modifyOvernightRestriction() {
      let overnightRestriction = {
        id: this.overallAccountId,
        insightType: "OVERNIGHT_RESTRICTION",
        newActiveState: this.overnightRestriction,
        newIsOvernightRestriction: this.overnightRestriction,
      };

      Repository.modifyOvernightRestriction(overnightRestriction).then(
        (response) => {
          if (response.status > 400)
            alert(
              "An error occurred by modifying the overnight restriction :("
            );
        }
      );
    },
    modifyTradingDays() {
      // Hour related
      let now = new Date(),
        start = new Date(),
        end = new Date();
      let startHourSplit = this.startHourSelected.split(":");
      let endHourSplit = this.endHourSelected.split(":");
      start.setHours(Number(startHourSplit[0]));
      start.setMinutes(Number(startHourSplit[1]));
      end.setHours(Number(endHourSplit[0]));
      end.setMinutes(Number(endHourSplit[1]));

      // Days related
      let selectedDays = [];
      this.tradingDays.forEach((day) => {
        if (day.trade) selectedDays.push(day.value.toUpperCase());
      });
      let calendarSettings = {
        id: this.overallAccountId,
        insightType: "TRADING_DAYS_RESTRICTION",
        newActiveState: true,
        offset: (now.getTimezoneOffset() / 60) * -1,
        startingHour: start,
        endingHour: end,
        tradingDays: selectedDays,
      };

      Repository.modifyTradingDaysRestriction(calendarSettings).then(
        (response) => {
          if (response.status > 400)
            alert("An error occurred by modifying the calendar settings :(");
        }
      );
    },
    truncateString(string, maxLength) {
      return string !== null && string.length > maxLength
        ? `${string.substring(0, maxLength)}...`
        : string;
    },
  },
};
</script>



<style scoped>
#app .tradingPlan-view .error {
  background-color: var(--v-darkRed-base) !important;
}
</style>
